import {
  CompanyQuery,
  OptionStatus,
  useCancelReversalMutation,
} from "@/generated/graphql_billing";
import { useIsSpMode } from "@/hooks";
import { color } from "@/styles/theme";
import { css } from "@emotion/react";
import { Font, Button } from "@moneyforward/mfx_biz_react_components";
import { endOfMonth, startOfDay } from "date-fns";
import { useRouter } from "next/router";
import { Dispatch, SetStateAction } from "react";
import { PageTitle } from "../molecules";
import useRollbarUtil from "../hooks/useRollbarUtil";
import { PlanDetailBlock } from "@/components/molecules/PlanDetailBlock";
import useFeatureFlags from "@/libs/hooks/useFeatureFlags";

type Props = {
  setIsCanecelPage: Dispatch<SetStateAction<boolean>>;
  company: CompanyQuery["company"];
  now: Date;
  /**
   * @description [FutureTask] After release "MFXID-2736", we should remove setIsCanecelPage and use onClickCancel instead. \
   * At that time, we should change onClickCancel optional to required. (it means remove "?" from onClickCancel: () => void;
   * @returns {void}
   */
  onClickCancel?: () => void;
};

export const PlanStopCancel = (props: Props) => {
  const {
    featureFlags,
    loading: featureFlagLoading,
    error: featureFlagError,
  } = useFeatureFlags();
  const { getNotifyErrorHandlerForGraphQL } = useRollbarUtil();
  const { setIsCanecelPage, now } = props;
  const company = props.company;
  const router = useRouter();

  const contractedPlan = company.canceledPlanContracts.find(
    (plan) =>
      startOfDay(new Date(plan.usingDateFrom)) <= now &&
      (!plan.usingDateTo || now <= endOfMonth(new Date(plan.usingDateTo)))
  );
  const contractedOptions = company.canceledOptionContracts
    ? company.canceledOptionContracts
        .map((contract) => contract.option)
        .filter((option) => option.status === OptionStatus.Active)
    : [];
  const params = {
    plan: {
      id: contractedPlan?.id,
      name: contractedPlan?.plan.name,
      services: contractedPlan?.plan.services.map((service) => ({
        id: service.id,
        name: service.name,
      })),
      price: contractedPlan?.plan.price,
    },
    options: contractedOptions.map((option) => ({
      id: option.id,
      name: option.name,
      price: option.price,
      optionTerms: option.optionTerms,
    })),
  };

  const isSpMode = useIsSpMode();
  const [cancelReversalMutation] = useCancelReversalMutation({
    onCompleted: (data) => {
      if (data.cancelReversal.result) {
        router.push("/plan-stop-cancel-completed");
      }
    },
    onError: getNotifyErrorHandlerForGraphQL("Failed to cancel reversal"),
    context: {
      api: "billing",
    },
  });

  const handleSubmit = async () => {
    await cancelReversalMutation();
  };
  return (
    <>
      <PageTitle
        mainTitle={"解約の取消し"}
        subTitle={
          <Font variant="l">
            現在予定されている解約を取消します。
            <br />
            解約前の下記の契約内容で引き続きご利用いただけます。
            <br />
            契約内容を変更したい場合は、解約取消し後に契約管理画面から変更してください。
          </Font>
        }
      />
      <div css={container(isSpMode)}>
        <div css={planBoxTop}>
          <PlanDetailBlock
            plan={contractedPlan.plan}
            isPlanPriceTaxExcluded={true}
            containerStyle={planDetailBlock}
          />
          {params.options.length > 0 && (
            <>
              <div css={border}></div>
              <div css={planDetail}>
                <Font variant="mb">オプション</Font>
                {params.options.map((option) => (
                  <div css={planList} key={option.id}>
                    <Font>{option.name}</Font>
                    <Font>{option.price.toLocaleString()}円/月</Font>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        <div css={button}>
          <div>
            <Button
              variant="secondary"
              type="button"
              onClick={() => {
                // [FutureTask] After release "MFXID-2736", we should remove setIsCanecelPage and use onClickCancel instead completely.
                // At that time, we should change onClickCancel optional to required. (it means remove "?" from onClickCancel: () => void;)
                if (featureFlags.CollectCancelReason.Enabled) {
                  props.onClickCancel && props.onClickCancel();
                } else {
                  setIsCanecelPage(false);
                }
              }}
            >
              キャンセル
            </Button>
          </div>
          <div>
            <Button
              variant="primary"
              type="button"
              onClick={async () => await handleSubmit()}
            >
              解約を取消す
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

const container = (isSpMode: boolean) =>
  css({
    width: isSpMode ? "auto" : "640px",
    margin: "36px auto 0 auto",
  });

const planBoxTop = css({
  border: `solid 1px ${color.gray300}`,
  borderRadius: "4px",
  backgroundColor: color.white,
  padding: "16px 24px",
  display: "flex",
  flexDirection: "column",
  gap: "16px",
});

const planDetail = css({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
});

const planDetailBlock = css({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  width: "100%",
});

const planList = css({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
});

const serviceList = css({
  display: "flex",
  alignItems: "center",
  gap: "8px",
});

const border = css({
  borderTop: `1px solid ${color.gray300}`,
});

const button = css({
  display: "flex",
  gap: "16px",
  justifyContent: "center",
  marginTop: "40px",
});
